import { useEffect, useRef, useState } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import TileLayer from '@arcgis/core/layers/TileLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
export function useMapCore({ config, tileLayers, areas = [], graphicsLayerRef, additionalLayers = [], }) {
    const mapDiv = useRef(null);
    const mapViewRef = useRef(null);
    const tileLayersRef = useRef([]);
    const [mapReady, setMapReady] = useState(false);
    const [basemap, setBasemap] = useState(config.basemap || 'arcgis-imagery');
    const [tileLayerVisibility, setTileLayerVisibility] = useState({});
    const getMapCenter = () => {
        if (config.center)
            return config.center;
        if (areas.length > 0) {
            const allPoints = [];
            areas.forEach(area => {
                area.geometry.forEach(polygon => {
                    polygon.forEach(ring => {
                        ring.forEach(point => {
                            allPoints.push(point);
                        });
                    });
                });
            });
            if (allPoints.length > 0) {
                const lons = allPoints.map(p => p[0]);
                const lats = allPoints.map(p => p[1]);
                const minLon = Math.min(...lons);
                const maxLon = Math.max(...lons);
                const minLat = Math.min(...lats);
                const maxLat = Math.max(...lats);
                return [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
            }
        }
        return undefined;
    };
    const handleZoomIn = () => {
        const mapView = mapViewRef.current;
        if (!mapView)
            return;
        mapView.goTo({ zoom: mapView.zoom + 1 }, { duration: 250 });
    };
    const handleZoomOut = () => {
        const mapView = mapViewRef.current;
        if (!mapView)
            return;
        mapView.goTo({ zoom: mapView.zoom - 1 }, { duration: 250 });
    };
    const handleRecenter = () => {
        const mapView = mapViewRef.current;
        if (!mapView)
            return;
        const center = getMapCenter();
        if (!center)
            return;
        mapView.goTo({
            center,
            zoom: config.zoom
        }, {
            duration: 1000,
            easing: 'ease-in-out'
        });
    };
    // Initialize the map
    useEffect(() => {
        if (!mapDiv.current)
            return;
        const initialBasemap = config.basemap || 'arcgis-imagery';
        const initialZoom = config.zoom;
        const initialCenter = getMapCenter();
        const initialUiComponents = config.uiComponents || [];
        const graphicsLayer = graphicsLayerRef.current || new GraphicsLayer();
        if (!graphicsLayerRef.current) {
            graphicsLayerRef.current = graphicsLayer;
        }
        const tileLayerInstances = tileLayers.map((layerConfig, index) => {
            var _a, _b, _c, _d, _e;
            const layer = new TileLayer({
                url: layerConfig.url,
                opacity: (_a = layerConfig.opacity) !== null && _a !== void 0 ? _a : 0.6,
                minScale: (_b = layerConfig.minScale) !== null && _b !== void 0 ? _b : 25000,
                maxScale: (_c = layerConfig.maxScale) !== null && _c !== void 0 ? _c : 0,
                visible: (_e = (_d = tileLayerVisibility[index]) !== null && _d !== void 0 ? _d : layerConfig.visible) !== null && _e !== void 0 ? _e : true
            });
            return layer;
        });
        tileLayersRef.current = tileLayerInstances;
        const map = new Map({
            basemap: initialBasemap,
            layers: [...tileLayerInstances, graphicsLayer, ...additionalLayers],
        });
        const mapView = new MapView({
            map,
            zoom: initialZoom,
            center: initialCenter,
            constraints: {
                rotationEnabled: false,
            },
            container: mapDiv.current,
            ui: {
                components: initialUiComponents,
            },
            attributionVisible: false,
            popupEnabled: false,
        });
        mapViewRef.current = mapView;
        mapView.when(() => {
            setMapReady(true);
        });
        return () => {
            mapView.destroy();
        };
    }, []);
    // Update basemap when changed
    useEffect(() => {
        const mapView = mapViewRef.current;
        if (!(mapView === null || mapView === void 0 ? void 0 : mapView.map))
            return;
        mapView.map.basemap = basemap;
    }, [basemap]);
    // Update tile layer visibility
    useEffect(() => {
        const layers = tileLayersRef.current;
        if (layers.length === 0)
            return;
        layers.forEach((layer, index) => {
            if (tileLayerVisibility[index] !== undefined) {
                layer.visible = tileLayerVisibility[index];
            }
        });
    }, [tileLayerVisibility]);
    // Update map center when config.center changes
    useEffect(() => {
        const mapView = mapViewRef.current;
        if (!mapView || !mapReady || !config.center)
            return;
        mapView.goTo({
            center: config.center,
            zoom: config.zoom
        }, {
            duration: 500,
            easing: 'ease-in-out'
        });
    }, [config.center, config.zoom, mapReady]);
    return {
        mapDiv,
        mapView: mapViewRef.current,
        mapReady,
        basemap,
        setBasemap,
        tileLayerVisibility,
        setTileLayerVisibility,
        tileLayersRef,
        handleZoomIn,
        handleZoomOut,
        handleRecenter,
        getMapCenter,
    };
}
